"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/Icon';

const links = [
  { href: '/', label: 'Home' },
  { href: '/dtc', label: 'DTC' },
  { href: '/projects', label: 'Projects' },
  { href: '/experience', label: 'Experience' },
  { href: '/tech-stack', label: 'Tech Stack' },
  { href: '/articles', label: 'Articles' },
];

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const navRef = useRef<HTMLElement>(null);

  const closeMenu = () => setIsMenuOpen(false);

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);

  // Close the menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;

    // Close menu when clicking outside or pressing Escape
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isMenuOpen]);

  return (
    <nav className="navbar" ref={navRef} aria-label="Main">
      <Link href="/" className="logo" aria-label="BEMOI home">BEMOI</Link>
      <div id="site-menu" className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
        <ul className="nav-list">
          {links.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                className={`nav-link ${isActive(link.href) ? 'active' : ''}`}
                aria-current={isActive(link.href) ? 'page' : undefined}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
        <Link href="/#contact" className="nav-cta" onClick={closeMenu}>
          Let&apos;s talk
          <Icon name="arrowRight" size={16} />
        </Link>
      </div>
      <button
        type="button"
        className={`hamburger ${isMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMenuOpen((open) => !open)}
        aria-expanded={isMenuOpen}
        aria-controls="site-menu"
        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
      >
        <span className="bar"></span>
        <span className="bar"></span>
        <span className="bar"></span>
      </button>
    </nav>
  );
}
